import { Badge, Button, Card, Group, PasswordInput, Stack, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { meApi } from '../api/auth';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  changePasswordSchema,
  emptyToNull,
  profileSchema,
  type ChangePasswordValues,
  type ProfileValues,
} from '../auth/schemas';
import { PageTitle } from '../components/PageTitle';
import { ru } from '../i18n/ru';
import { RequisitesCard } from '../studio/RequisitesCard';
import { ACCENT } from '../theme';

export function ProfilePage() {
  const { user, setUser, logout, hasRole } = useAuth();
  const navigate = useNavigate();

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      lastName: user?.lastName ?? '',
      firstName: user?.firstName ?? '',
      middleName: user?.middleName ?? '',
      phone: user?.phone ?? '',
    },
  });

  const passwordForm = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', repeatPassword: '' },
  });

  const onSaveProfile = profileForm.handleSubmit(async (values) => {
    try {
      const updated = await meApi.update({
        lastName: values.lastName.trim(),
        firstName: values.firstName.trim(),
        middleName: emptyToNull(values.middleName),
        phone: emptyToNull(values.phone),
      });
      setUser(updated);
      notifications.show({ color: 'green', message: ru.profile.saved });
    } catch (error) {
      notifications.show({ color: 'red', title: ru.common.error, message: errorMessage(error) });
    }
  });

  const onChangePassword = passwordForm.handleSubmit(async (values) => {
    try {
      await meApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      // Сервер отзывает все сессии, поэтому продолжать работу под старым
      // токеном нельзя — сразу уводим на страницу входа.
      notifications.show({ color: 'green', message: ru.profile.passwordChanged });
      await logout();
      void navigate('/login', { replace: true });
    } catch (error) {
      notifications.show({ color: 'red', title: ru.common.error, message: errorMessage(error) });
    }
  });

  if (user === null) {
    return null;
  }

  return (
    <Stack gap="xl" maw={520}>
      <Stack gap="md">
        <PageTitle title={ru.profile.title} />
        <Group gap="xs">
          {user.roles.map((role) => (
            <Badge key={role} color={ACCENT} variant={role === 'Admin' ? 'filled' : 'light'}>
              {role === 'Admin' ? ru.profile.roleAdmin : ru.profile.roleParent}
            </Badge>
          ))}
        </Group>
      </Stack>

      <Card padding="lg">
        <form onSubmit={(event) => void onSaveProfile(event)} noValidate>
          <Stack gap="md">
            <Title order={3}>{ru.profile.dataTitle}</Title>

            <TextInput label={ru.fields.email} value={user.email} disabled />
            <TextInput
              label={ru.fields.lastName}
              error={profileForm.formState.errors.lastName?.message}
              {...profileForm.register('lastName')}
            />
            <TextInput
              label={ru.fields.firstName}
              error={profileForm.formState.errors.firstName?.message}
              {...profileForm.register('firstName')}
            />
            <TextInput
              label={ru.fields.middleName + ru.fields.optional}
              error={profileForm.formState.errors.middleName?.message}
              {...profileForm.register('middleName')}
            />
            <TextInput
              label={ru.fields.phone + ru.fields.optional}
              type="tel"
              placeholder="+7 900 123-45-67"
              error={profileForm.formState.errors.phone?.message}
              {...profileForm.register('phone')}
            />

            <Button type="submit" loading={profileForm.formState.isSubmitting} w="fit-content">
              {ru.profile.save}
            </Button>
          </Stack>
        </form>
      </Card>

      <Card padding="lg">
        <form onSubmit={(event) => void onChangePassword(event)} noValidate>
          <Stack gap="md">
            <Title order={3}>{ru.profile.passwordTitle}</Title>

            <PasswordInput
              label={ru.fields.currentPassword}
              autoComplete="current-password"
              error={passwordForm.formState.errors.currentPassword?.message}
              {...passwordForm.register('currentPassword')}
            />
            <PasswordInput
              label={ru.fields.newPassword}
              autoComplete="new-password"
              description={ru.auth.passwordRule}
              error={passwordForm.formState.errors.newPassword?.message}
              {...passwordForm.register('newPassword')}
            />
            <PasswordInput
              label={ru.fields.repeatPassword}
              autoComplete="new-password"
              error={passwordForm.formState.errors.repeatPassword?.message}
              {...passwordForm.register('repeatPassword')}
            />

            <Button type="submit" loading={passwordForm.formState.isSubmitting} w="fit-content">
              {ru.profile.changePassword}
            </Button>
          </Stack>
        </form>
      </Card>

      {hasRole('Admin') && <RequisitesCard />}
    </Stack>
  );
}
